import React, { useEffect, useState } from 'react';
import crmClient from './crmClient';
import { fetchSubscriptionMetrics } from '../../services/paymentService';
import { formatCurrency, formatNumber } from './format';

const TIER_LABELS = {
  free: 'Free',
  founding: 'Grandfathered',
  pro: 'Pro',
  enterprise: 'Enterprise',
  legacy: 'Legacy',
};
const TIER_TONES = {
  free: '',
  founding: 'teal',
  pro: 'green',
  enterprise: 'blue',
};
const TIER_COLORS = {
  free: '#8a94a6',
  founding: '#2dce89',
  pro: '#2dce89',
  enterprise: '#1d8cf8',
  legacy: '#fba540',
};

const Kpi = ({ label, value, sub, tone }) => (
  <div className={`crm-kpi${tone ? ` crm-kpi--${tone}` : ''}`}>
    <span>{label}</span>
    <strong>{value}</strong>
    {sub && <small>{sub}</small>}
  </div>
);

const fmtDate = value => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const tierLabel = tier => TIER_LABELS[tier] || tier || '—';

const SubscriptionsView = () => {
  const [data, setData] = useState(null);
  const [stripe, setStripe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    let active = true;
    setLoading(true);
    crmClient
      .subscriptionMetrics()
      .then(payload => {
        if (active) {
          setData(payload);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setError('Unable to load subscription metrics.');
          setLoading(false);
        }
      });
    fetchSubscriptionMetrics()
      .then(payload => { if (active) setStripe(payload); })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  if (loading && !data) {
    return <div className="crm-empty">Loading subscription metrics…</div>;
  }
  if (error && !data) {
    return <div className="crm-empty crm-empty--error">{error}</div>;
  }

  const totals = (data && data.totals) || {};
  const tiers = (data && data.tiers) || [];
  const accounts = (data && data.accounts) || [];
  const changes = (data && data.recent_changes) || [];
  const subs = (stripe && stripe.subscriptions) || [];

  const isTrialing = acc => acc.trial_ends_at && new Date(acc.trial_ends_at).getTime() > Date.now();

  // Fold legacy Stripe subscriptions (no entitlement row) into the same account
  // list, tagged by their plan or 'legacy' when they predate the tier system.
  const accountStoreIds = new Set(accounts.map(a => a.store_id));
  const legacyRows = subs
    .filter(s => !s.store_id || !accountStoreIds.has(s.store_id))
    .map(s => ({
      store_id: s.subscription_id,
      business_name: s.name || s.email || '—',
      tier: s.plan || 'legacy',
      is_comp: false,
      lifetime_orders: null,
      updated_at: s.created,
      _stripe: true,
      _status: s.status,
    }));
  const allAccounts = accounts.concat(legacyRows);

  const matchesType = acc => {
    if (typeFilter === 'all') return true;
    if (typeFilter === 'comp') return Boolean(acc.is_comp);
    if (typeFilter === 'trialing') return isTrialing(acc);
    return acc.tier === typeFilter;
  };
  const filteredAccounts = allAccounts.filter(matchesType);

  const statusText = acc => {
    if (acc.is_comp) return 'Comp (internal)';
    if (acc._stripe) return acc._status && acc._status !== 'active' ? acc._status : 'Paying';
    if (isTrialing(acc)) return `Trial ends ${fmtDate(acc.trial_ends_at)}`;
    if (acc.tier === 'pro' || acc.tier === 'enterprise') return 'Paying';
    return '—';
  };

  return (
    <div className="crm-reports">
      <div className="crm-kpi-grid">
        <Kpi label="Monthly recurring (MRR)" value={formatCurrency(stripe ? stripe.mrr : totals.mrr)} tone="green" sub={stripe ? `${formatCurrency(stripe.arr)} ARR · ${stripe.active_subscriptions} active Stripe subs` : `${formatCurrency(totals.arr)} ARR`} />
        <Kpi label="Paid accounts" value={formatNumber(totals.paid_stores)} tone="blue" sub={`${totals.paid_conversion_pct || 0}% of ${formatNumber(totals.total_stores)} tracked`} />
        <Kpi label="Grandfathered" value={formatNumber(totals.grandfathered)} tone="teal" sub="founding ($0) — upgrade targets" />
        <Kpi label="Trialing" value={formatNumber(totals.trialing)} tone="teal" sub="free trials in flight" />
        <Kpi label="Comp (internal)" value={formatNumber(totals.comp_stores)} sub="paid tier, not billed" />
        <Kpi label="Upgrades · 30d" value={formatNumber(totals.upgrades_30d)} sub={`${formatNumber(totals.upgrades_90d)} in 90d`} />
        <Kpi label="Free" value={formatNumber(totals.free_stores)} sub="no plan yet" />
      </div>

      <div className="crm-card">
        <h3>Revenue by tier</h3>
        <div className="crm-kpi-grid">
          {tiers.map(t => (
            <Kpi
              key={t.tier}
              label={tierLabel(t.tier)}
              value={formatNumber(t.stores)}
              tone={TIER_TONES[t.tier]}
              sub={`${t.price ? `$${t.price}/mo` : '$0'}${t.billable_stores ? ` · ${formatCurrency(t.mrr)} MRR` : ''}${t.comp_stores ? ` · ${t.comp_stores} comp` : ''}${t.trialing ? ` · ${t.trialing} trialing` : ''}`}
            />
          ))}
        </div>
      </div>

      <div className="crm-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Subscriptions &amp; accounts</h3>
          <label className="crm-date-filter">
            <span>Type</span>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
              <option value="all">All types</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
              <option value="legacy">Legacy</option>
              <option value="founding">Grandfathered</option>
              <option value="free">Free</option>
              <option value="trialing">Trialing</option>
              <option value="comp">Comp (internal)</option>
            </select>
          </label>
        </div>
        {filteredAccounts.length === 0 ? (
          <p className="crm-muted">No accounts match this filter.</p>
        ) : (
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Tier</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Lifetime orders</th>
                  <th>Location</th>
                  <th>Since</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map(acc => (
                  <tr key={acc.store_id}>
                    <td><strong>{acc.business_name || '—'}</strong></td>
                    <td><span className="crm-chip" style={{ color: TIER_COLORS[acc.tier] || 'inherit', borderColor: TIER_COLORS[acc.tier] || undefined }}>{tierLabel(acc.tier)}</span></td>
                    <td>{statusText(acc)}</td>
                    <td style={{ textAlign: 'right' }}>{acc._stripe ? '—' : formatNumber(acc.lifetime_orders || 0)}</td>
                    <td>{[acc.city, acc.state].filter(Boolean).join(', ') || '—'}</td>
                    <td>{fmtDate(acc.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="crm-card">
        <h3>Recent plan changes</h3>
        {changes.length === 0 ? (
          <p className="crm-muted">
            No plan changes logged yet. Every upgrade, downgrade, and trial from now on is recorded here with the account and
            timestamp — so you can correlate conversions with the campaigns and touches that drove them, and repeat what works.
          </p>
        ) : (
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Change</th>
                  <th>By</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {changes.map((c, i) => (
                  <tr key={`${c.store_id}-${i}`}>
                    <td>{c.business_name || '—'}</td>
                    <td>{tierLabel(c.previous_tier)} → <strong>{tierLabel(c.new_tier)}</strong>{c.reason ? ` (${c.reason})` : ''}</td>
                    <td>{c.changed_by || '—'}</td>
                    <td>{fmtDate(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionsView;
